/**
 * Light/dark theme switcher.
 *
 *   resolvedMode  — "light" or "dark", what the MUI theme actually is.
 *   preference    — "light" / "dark" / "system" — what the user picked.
 *
 * Stores preference in localStorage under `engage_color_mode`. Falls back
 * to the OS preference when the user picks "system" or hasn't picked
 * anything yet, and listens for OS-level changes via `matchMedia`.
 *
 * Designed to be lifted into other Paybyrd apps unchanged.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { ThemeProvider } from "@mui/material/styles";

import { buildTheme, type ColorMode } from "../../lib/theme";

export type ColorModePreference = ColorMode | "system";

interface ColorModeContextValue {
  preference: ColorModePreference;
  resolvedMode: ColorMode;
  setPreference: (p: ColorModePreference) => void;
  /** Cycle through light → dark → system in one click — most users prefer this to a 3-way picker. */
  toggle: () => void;
}

const STORAGE_KEY = "engage_color_mode";

const ColorModeContext = createContext<ColorModeContextValue | null>(null);

function readStoredPreference(): ColorModePreference {
  const raw = (typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY)) || "system";
  return raw === "light" || raw === "dark" ? raw : "system";
}

function readSystemMode(): ColorMode {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ColorModeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ColorModePreference>(readStoredPreference);
  const [systemMode, setSystemMode] = useState<ColorMode>(readSystemMode);

  // React to OS-level theme changes when preference is "system".
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => setSystemMode(e.matches ? "dark" : "light");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const resolvedMode: ColorMode = preference === "system" ? systemMode : preference;

  const setPreference = useCallback((p: ColorModePreference) => {
    setPreferenceState(p);
    try {
      localStorage.setItem(STORAGE_KEY, p);
    } catch {
      /* private mode etc. — non-fatal */
    }
  }, []);

  const toggle = useCallback(() => {
    setPreferenceState((cur) => {
      const next: ColorModePreference =
        cur === "light" ? "dark" : cur === "dark" ? "system" : "light";
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* */
      }
      return next;
    });
  }, []);

  // Build the theme once per mode swap. createTheme is cheap; memoizing
  // avoids a fresh object every render which would cascade re-renders
  // through every styled component.
  const theme = useMemo(() => buildTheme(resolvedMode), [resolvedMode]);

  // Reflect the mode on <html> as a data attribute so any non-MUI CSS
  // (custom emails preview, third-party widgets) can react to it.
  useEffect(() => {
    document.documentElement.dataset.theme = resolvedMode;
    document.documentElement.style.colorScheme = resolvedMode;
  }, [resolvedMode]);

  const value = useMemo<ColorModeContextValue>(
    () => ({ preference, resolvedMode, setPreference, toggle }),
    [preference, resolvedMode, setPreference, toggle],
  );

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export function useColorMode(): ColorModeContextValue {
  const v = useContext(ColorModeContext);
  if (!v) throw new Error("useColorMode must be used inside <ColorModeProvider>");
  return v;
}
